// Route: /tutor/skills — библиотека навыков тьютора (CRUD каталога).
//
// Навыки НЕ активируются на этой странице. Активация — per-session в чате через
// popover модификаторов (✦) у поля ввода: он пишет выбор в tutor_sessions.skill_ids,
// а stream.handler подставляет инструкции активных навыков в системный промпт.
// Эта страница только управляет каталогом: создать / отредактировать / удалить
// свои навыки. Встроенные навыки — read-only.
//
// См. docs/ui-spec/tutor.skills.{desktop,mobile}.md.

import { tolerantSearch } from "@studyhub/shared/utils/tolerant-search";
import { Button, SectionHeader } from "@studyhub/ui";
import { IconTooltip } from "@studyhub/ui/components/tooltip";
import { toast } from "@studyhub/ui/toast";
import { IconBolt, IconChevronLeft, IconPlus } from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { LIST_PAGE_GRID, LIST_PAGE_SHELL, ListToolbar } from "@/components/shared/entity-list";
import { SkillCard } from "@/components/tutor/skills/skill-card";
import { SkillFormDialog, type SkillFormValues } from "@/components/tutor/skills/skill-form-dialog";
import { useConfirm } from "@/hooks/use-confirm";
import { useOnlineStatus } from "@/hooks/use-online-status";
import {
	type TutorSkillDto,
	useCreateSkill,
	useDeleteSkill,
	useTutorSkills,
	useUpdateSkill,
} from "@/hooks/use-tutor-skills";

const searchSchema = z.object({
	filter: z.enum(["all", "builtin", "mine"]).optional(),
	q: z.string().optional(),
});

export const Route = createFileRoute("/tutor/skills")({
	validateSearch: tolerantSearch(searchSchema),
	component: SkillsPage,
});

function SkillsPage(): React.ReactElement {
	const { t, i18n } = useTranslation(["tutor", "common"]);
	const { t: tTrash } = useTranslation("trash");
	const confirm = useConfirm();
	const online = useOnlineStatus();
	const locale = i18n.language.slice(0, 2);
	const search = Route.useSearch();
	const [q, setQ] = useState(search.q ?? "");
	const skillsQuery = useTutorSkills(locale);
	const createMut = useCreateSkill();
	const updateMut = useUpdateSkill();
	const deleteMut = useDeleteSkill();

	const [formOpen, setFormOpen] = useState(false);
	const [editing, setEditing] = useState<TutorSkillDto | null>(null);

	const rawSkills = useMemo(() => skillsQuery.data?.skills ?? [], [skillsQuery.data]);

	const filter = search.filter ?? "all";
	const filtered = useMemo(() => {
		let items = rawSkills;
		if (filter === "builtin") items = items.filter((s) => s.isBuiltin);
		if (filter === "mine") items = items.filter((s) => !s.isBuiltin);
		if (q) {
			const query = q.toLowerCase();
			items = items.filter(
				(s) =>
					s.name.toLowerCase().includes(query) ||
					(s.description ?? "").toLowerCase().includes(query),
			);
		}
		return items;
	}, [rawSkills, filter, q]);

	function openCreate() {
		setEditing(null);
		setFormOpen(true);
	}
	function openEdit(skill: TutorSkillDto) {
		setEditing(skill);
		setFormOpen(true);
	}

	async function handleSubmit(values: SkillFormValues): Promise<void> {
		try {
			if (editing) {
				await updateMut.mutateAsync({
					id: editing.id,
					name: values.name,
					description: values.description,
					instruction: values.instruction,
				});
				toast.success(t("skills.toast.updated", { defaultValue: "Навык обновлён" }));
			} else {
				await createMut.mutateAsync({
					name: values.name,
					description: values.description,
					instruction: values.instruction,
					locale,
				});
				toast.success(t("skills.toast.created", { defaultValue: "Навык создан" }));
			}
			setFormOpen(false);
			setEditing(null);
		} catch (err) {
			toast.error(
				err instanceof Error
					? err.message
					: t("skills.toast.saveFailed", { defaultValue: "Не удалось сохранить" }),
			);
		}
	}

	async function handleDelete(skill: TutorSkillDto): Promise<void> {
		if (!online) {
			toast.error(tTrash("offline.action"));
			return;
		}
		const ok = await confirm({
			title: t("skills.deleteConfirm.title", { defaultValue: "Удалить навык?" }),
			description: t("skills.deleteConfirm.body", { name: skill.name }),
			confirmLabel: t("common:actions.delete"),
			tone: "destructive",
		});
		if (!ok) return;
		try {
			await deleteMut.mutateAsync(skill.id);
			toast.success(t("skills.toast.deleted", { defaultValue: "Навык удалён" }));
		} catch (err) {
			toast.error(
				err instanceof Error
					? err.message
					: t("skills.toast.deleteFailed", { defaultValue: "Не удалось удалить" }),
			);
		}
	}

	const builtin = filtered.filter((s) => s.isBuiltin);
	const mine = filtered.filter((s) => !s.isBuiltin);

	return (
		<div className={LIST_PAGE_SHELL}>
			<div className="flex items-center gap-1">
				<IconTooltip label={t("chat.back", { defaultValue: "Назад" })}>
					<Button
						variant="ghost"
						size="icon"
						asChild
						className="size-8 shrink-0 cursor-pointer"
						aria-label={t("chat.back", { defaultValue: "Назад" })}
					>
						<Link to="/tutor">
							<IconChevronLeft size={16} />
						</Link>
					</Button>
				</IconTooltip>
				<SectionHeader
					className="flex-1"
					icon={<IconBolt />}
					category="ai"
					title={t("skillsPage.title", { defaultValue: "Навыки" })}
					description={t("skillsPage.subtitle", {
						defaultValue:
							"A library of skills — short instructions that shape the answer style. Enable them in any chat via the modifiers button (✦) next to the input.",
					})}
					actions={
						<Button onClick={openCreate} className="cursor-pointer">
							<IconPlus size={16} />
							<span className="hidden sm:inline">
								{t("skillsPage.create", { defaultValue: "Создать" })}
							</span>
						</Button>
					}
				/>
			</div>

			{/* Тулбар — унифицирован через ListToolbar (поиск; без grid/list:
			    навыки сгруппированы секциями «Встроенные» / «Мои»). */}
			<ListToolbar
				search={{
					value: q,
					onChange: setQ,
					placeholder: t("skillsPage.search", { defaultValue: "Поиск..." }),
				}}
				showViewToggle={false}
			/>

			{builtin.length > 0 && (
				<section>
					<h2 className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
						{t("skillsPage.builtin", { defaultValue: "Встроенные" })}
					</h2>
					<div className={LIST_PAGE_GRID}>
						{builtin.map((s) => (
							<SkillCard key={s.id} skill={s} />
						))}
					</div>
				</section>
			)}

			<section>
				<h2 className="text-2xs font-semibold uppercase tracking-wider text-muted-foreground mb-2">
					{t("skillsPage.mine", { defaultValue: "Мои" })}
				</h2>
				{mine.length > 0 ? (
					<div className={LIST_PAGE_GRID}>
						{mine.map((s) => (
							<SkillCard
								key={s.id}
								skill={s}
								onEdit={() => openEdit(s)}
								onDelete={() => void handleDelete(s)}
							/>
						))}
					</div>
				) : (
					<div className="flex flex-col items-center gap-3 rounded-[var(--radius-panel)] border border-border bg-card p-8 text-center shadow-e1">
						<div className="inline-flex size-12 items-center justify-center rounded-[var(--radius-tile)] tint-amber [box-shadow:inset_0_1px_0_rgb(255_255_255/0.4)]">
							<IconBolt size={22} />
						</div>
						<p className="text-sm font-semibold text-foreground">
							{t("skillsPage.empty.title", { defaultValue: "Создай свой навык" })}
						</p>
						<p className="max-w-md text-xs text-muted-foreground">
							{t("skillsPage.empty.hint", {
								defaultValue: "A short instruction you can enable in any tutor chat.",
							})}
						</p>
						<Button variant="outline" onClick={openCreate} className="cursor-pointer">
							<IconPlus size={14} />
							{t("skillsPage.empty.cta", { defaultValue: "Создать" })}
						</Button>
					</div>
				)}
			</section>

			{/* Create / Edit dialog */}
			<SkillFormDialog
				open={formOpen}
				onOpenChange={(o) => {
					setFormOpen(o);
					if (!o) setEditing(null);
				}}
				mode={editing ? "edit" : "create"}
				initialValues={
					editing
						? {
								name: editing.name,
								description: editing.description ?? "",
								instruction: editing.instruction,
							}
						: undefined
				}
				isSubmitting={createMut.isPending || updateMut.isPending}
				onSubmit={handleSubmit}
			/>
		</div>
	);
}
